import React, { useEffect, useRef, useCallback } from 'react';

interface AccessibleModalProps {
    /** Whether the modal is open */
    open: boolean;
    /** Called when the modal should close (Escape key or backdrop click) */
    onClose: () => void;
    /** Accessible title for the modal — used as aria-label */
    title: string;
    /** Optional: id for aria-labelledby if you have a visible heading */
    labelledById?: string;
    /** Extra classes for the backdrop */
    backdropClassName?: string;
    /** Extra classes for the modal panel itself */
    panelClassName?: string;
    /** Children rendered inside the panel */
    children: React.ReactNode;
    /** If true, clicking backdrop will NOT close (default: false) */
    persistent?: boolean;
}

/**
 * Accessible modal dialog wrapper.
 * Provides: role="dialog", aria-modal, focus trapping, Escape-to-close,
 * click-outside-to-close, body scroll lock, and auto-focus.
 */
const AccessibleModal: React.FC<AccessibleModalProps> = ({
    open,
    onClose,
    title,
    labelledById,
    backdropClassName = '',
    panelClassName = '',
    children,
    persistent = false,
}) => {
    const panelRef = useRef<HTMLDivElement>(null);
    const previousFocusRef = useRef<HTMLElement | null>(null);

    // --- Lock body scroll & store previous focus ---
    useEffect(() => {
        if (open) {
            previousFocusRef.current = document.activeElement as HTMLElement;
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [open]);

    // --- Auto-focus first focusable element ---
    useEffect(() => {
        if (!open || !panelRef.current) return;
        const focusable = panelRef.current.querySelectorAll<HTMLElement>(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length > 0) {
            focusable[0].focus();
        } else {
            panelRef.current.focus();
        }
    }, [open]);

    // --- Return focus on close ---
    useEffect(() => {
        if (!open && previousFocusRef.current) {
            previousFocusRef.current.focus();
            previousFocusRef.current = null;
        }
    }, [open]);

    // --- Escape to close ---
    useEffect(() => {
        if (!open) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                onClose();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [open, onClose]);

    // --- Focus trapping ---
    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key !== 'Tab' || !panelRef.current) return;

            const focusable = panelRef.current.querySelectorAll<HTMLElement>(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            if (focusable.length === 0) return;

            const first = focusable[0];
            const last = focusable[focusable.length - 1];

            if (e.shiftKey) {
                if (document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                }
            } else {
                if (document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        },
        []
    );

    if (!open) return null;

    return (
        <div
            className={`fixed inset-0 z-50 flex items-center justify-center ${backdropClassName}`}
            onClick={persistent ? undefined : onClose}
            role="presentation"
        >
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-label={labelledById ? undefined : title}
                aria-labelledby={labelledById || undefined}
                tabIndex={-1}
                className={panelClassName}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={handleKeyDown}
            >
                {children}
            </div>
        </div>
    );
};

export default AccessibleModal;
